/**
 * P&ID 跨线桥接（line hop）
 *
 * 规范：两根管线交叉但不相连时，横线在交点处跳半圆越过竖线，表明两线不连通。
 * 实现：扫描 React Flow 渲染出的边路径，找到 横×竖 的交叉点，
 * 把横线的路径 d 在交点处改写为 L + 半圆弧(A) + L；
 * 用 MutationObserver 在 React Flow 重绘边后自动重新应用。
 * 桥接写在本地图形坐标（d 属性）里，缩放/平移不影响；节点拖动导致重新布线后会被观察器补回。
 */

const HOP_R = 7; // 桥接半圆半径（大于最粗线宽 3.2）
const ENDPOINT_MARGIN = 8; // 交点距任一线段端点的最小距离（小于此视为相接/共点，不桥接）

interface Cmd {
  c: string;
  n: number[];
}

interface Seg {
  pathIdx: number;
  cmdIdx: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

function tokenize(d: string): Cmd[] {
  const out: Cmd[] = [];
  const re = /([MLQCAZ])([^MLQCAZ]*)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(d))) {
    out.push({ c: m[1], n: (m[2].match(/-?[\d.]+/g) ?? []).map(Number) });
  }
  return out;
}

function serialize(cmds: Cmd[]): string {
  return cmds.map((c) => c.c + c.n.map((v) => +v.toFixed(2)).join(' ')).join('');
}

/** 从命令序列收集直角线段（L），并跟踪游标 */
function collectSegs(cmds: Cmd[], pathIdx: number): Seg[] {
  const segs: Seg[] = [];
  let cx = 0;
  let cy = 0;
  cmds.forEach((c, cmdIdx) => {
    if (c.c === 'M' && c.n.length >= 2) {
      cx = c.n[0];
      cy = c.n[1];
    } else if (c.c === 'L' && c.n.length >= 2) {
      segs.push({ pathIdx, cmdIdx, x1: cx, y1: cy, x2: c.n[0], y2: c.n[1] });
      cx = c.n[0];
      cy = c.n[1];
    } else if (c.c === 'Q' && c.n.length >= 4) {
      cx = c.n[2];
      cy = c.n[3];
    } else if (c.c === 'A' && c.n.length >= 7) {
      cx = c.n[5];
      cy = c.n[6];
    } else if (c.c === 'Z') {
      cx = 0;
      cy = 0;
    }
  });
  return segs;
}

const EPS = 0.5;
const isH = (s: Seg) => Math.abs(s.y1 - s.y2) < EPS;
const isV = (s: Seg) => Math.abs(s.x1 - s.x2) < EPS;

/** 对单条路径重建 d：在记录的桥接点处插入半圆弧（横线向上跳） */
function rebuild(cmds: Cmd[], hopsByCmd: Map<number, number[]>): Cmd[] {
  const out: Cmd[] = [];
  let cx = 0;
  let cy = 0;
  cmds.forEach((c, cmdIdx) => {
    if (c.c === 'L' && c.n.length >= 2 && hopsByCmd.has(cmdIdx)) {
      const x1 = cx;
      const y1 = cy;
      const x2 = c.n[0];
      const y2 = c.n[1];
      const movingRight = x2 > x1;
      if (isH({ x1, y1, x2, y2 } as Seg)) {
        const xs = (hopsByCmd.get(cmdIdx) ?? [])
          .slice()
          .sort((p, q) => (movingRight ? p - q : q - p));
        let cursor = x1;
        for (const hopX of xs) {
          const r = Math.max(
            3,
            Math.min(HOP_R, Math.abs(hopX - cursor) - 1, Math.abs(hopX - x2) - 1),
          );
          const before = hopX - (movingRight ? r : -r);
          const after = hopX + (movingRight ? r : -r);
          out.push({ c: 'L', n: [before, y1] });
          out.push({ c: 'A', n: [r, r, 0, 0, movingRight ? 1 : 0, after, y1] });
          cursor = after;
        }
        out.push({ c: 'L', n: [x2, y2] });
        cx = x2;
        cy = y2;
        return;
      }
    }
    // 其余命令原样输出并推进游标
    out.push(c);
    if (c.c === 'M' && c.n.length >= 2) {
      cx = c.n[0];
      cy = c.n[1];
    } else if (c.c === 'L' && c.n.length >= 2) {
      cx = c.n[0];
      cy = c.n[1];
    } else if (c.c === 'Q' && c.n.length >= 4) {
      cx = c.n[2];
      cy = c.n[3];
    } else if (c.c === 'A' && c.n.length >= 7) {
      cx = c.n[5];
      cy = c.n[6];
    }
  });
  return out;
}

/** 扫描全部边路径，应用跨线桥接；返回被改写的路径条数 */
export function applyPidHops(): number {
  let applied = 0;
  const pathEls = Array.from(
    document.querySelectorAll<SVGPathElement>('.react-flow__edge-path'),
  );
  const all: { cmds: Cmd[]; segs: Seg[]; el: SVGPathElement; d: string }[] = [];
  pathEls.forEach((el) => {
    const d = el.getAttribute('d');
    if (!d) return;
    const cmds = tokenize(d);
    all.push({ cmds, segs: collectSegs(cmds, all.length), el, d });
  });
  // 重新编号（上面 push 时用的临时序号即最终序号）
  all.forEach((p, i) => p.segs.forEach((s) => (s.pathIdx = i)));

  for (let i = 0; i < all.length; i++) {
    const hopsByCmd = new Map<number, Set<number>>();
    const others = all.flatMap((p, j) => (j === i ? [] : p.segs));
    for (const s of all[i].segs) {
      for (const t of others) {
        let cx: number | null = null;
        let cy: number | null = null;
        if (isH(s) && isV(t)) {
          cx = t.x1;
          cy = s.y1;
        } else if (isV(s) && isH(t)) {
          // 交叉点同处，但桥接一律让横线跳：跳过（横线那边自己会处理）
          continue;
        } else {
          continue;
        }
        if (cx === null || cy === null) continue;
        // 交点必须严格位于两条线段内部（留端点余量：相接/共把手不算交叉）
        const minX = Math.min(s.x1, s.x2) + ENDPOINT_MARGIN;
        const maxX = Math.max(s.x1, s.x2) - ENDPOINT_MARGIN;
        const minY = Math.min(t.y1, t.y2) + ENDPOINT_MARGIN;
        const maxY = Math.max(t.y1, t.y2) - ENDPOINT_MARGIN;
        if (cx <= minX || cx >= maxX || cy <= minY || cy >= maxY) continue;
        const set = hopsByCmd.get(s.cmdIdx) ?? new Set<number>();
        set.add(+cx.toFixed(1));
        hopsByCmd.set(s.cmdIdx, set);
      }
    }
    if (hopsByCmd.size === 0) continue;
    const plain = new Map<number, number[]>();
    hopsByCmd.forEach((set, k) => plain.set(k, Array.from(set)));
    const rebuilt = rebuild(all[i].cmds, plain);
    const nd = serialize(rebuilt);
    if (nd !== all[i].d) {
      all[i].el.setAttribute('d', nd);
      applied++;
    }
  }
  return applied;
}

let scheduled: ReturnType<typeof setTimeout> | null = null;

/** 安装观察器：React Flow 重新布线后自动补回桥接 */
export function installPidHops(): void {
  const run = () => {
    try {
      applyPidHops();
    } catch {
      /* 忽略单次失败，观察器会重试 */
    }
  };
  const mo = new MutationObserver(() => {
    if (scheduled) clearTimeout(scheduled);
    scheduled = setTimeout(run, 120);
  });
  mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['d'] });
  (window as unknown as { __applyPidHops: typeof applyPidHops }).__applyPidHops = applyPidHops;
  setTimeout(run, 500);
}
